// frontend/src/i18n/index.js — i18n setup with RTL support + dynamic DB translations
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import ar from './locales/ar.json';
import bn from './locales/bn.json';
import fr from './locales/fr.json';

export const LANGUAGES = [
  { code: 'en', name: 'English',  nativeName: 'English',   rtl: false, flag: '🇬🇧' },
  { code: 'ar', name: 'Arabic',   nativeName: 'العربية',   rtl: true,  flag: '🇸🇦' },
  { code: 'bn', name: 'Bengali',  nativeName: 'বাংলা',    rtl: false, flag: '🇧🇩' },
  { code: 'fr', name: 'French',   nativeName: 'Français',  rtl: false, flag: '🇫🇷' },
];

export const isRTL = (lang) => LANGUAGES.find(l => l.code === lang)?.rtl || false;

const LANG_FONTS = {
  en: "'DM Sans', sans-serif",
  ar: "'DM Sans', 'Segoe UI', Tahoma, Arial, sans-serif",
  bn: "'Tiro Bangla', 'Noto Sans Bengali', 'Shonar Bangla', serif",
  fr: "'DM Sans', sans-serif",
};

export const applyDirection = (lang) => {
  const rtl = isRTL(lang);
  document.documentElement.dir  = rtl ? 'rtl' : 'ltr';
  document.documentElement.lang = lang;
  document.documentElement.style.setProperty('--sidebar-side',    rtl ? 'right' : 'left');
  document.documentElement.style.setProperty('--text-align',      rtl ? 'right' : 'left');
  document.documentElement.style.setProperty('--flex-direction',  rtl ? 'row-reverse' : 'row');
  const font = LANG_FONTS[lang] || LANG_FONTS.en;
  document.documentElement.style.setProperty('--font-body', font);
  document.body.style.fontFamily = font;
};

// Rebuild nested object from dot-notation flat keys
// e.g. { "nav.dashboard": "X" } → { nav: { dashboard: "X" } }
function unflatten(flat) {
  const out = {};
  for (const [dotKey, value] of Object.entries(flat)) {
    const parts = dotKey.split('.');
    let node = out;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!node[parts[i]] || typeof node[parts[i]] !== 'object') node[parts[i]] = {};
      node = node[parts[i]];
    }
    node[parts[parts.length - 1]] = value;
  }
  return out;
}

// Deep-merge override into base
function deepMerge(base, override) {
  const out = { ...base };
  for (const [k, v] of Object.entries(override)) {
    if (v && typeof v === 'object' && !Array.isArray(v) && base[k] && typeof base[k] === 'object') {
      out[k] = deepMerge(base[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

const BASE_RESOURCES = { en, ar, bn, fr };

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
      bn: { translation: bn },
      fr: { translation: fr },
    },
    lng: localStorage.getItem('staynivo-lang') || 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    // Bundled resources are already in memory — initialize synchronously so
    // useTranslation() never suspends on the first React render and causes
    // the outer Suspense boundary to fall back, which would make the splash
    // screen dehydrate and replay when the i18n Promise resolves.
    initImmediate: false,
    react: {
      // Also re-render on 'added' so that loadDynamicTranslations()
      // → addResourceBundle() immediately updates components without
      // requiring a language switch or page reload.
      bindI18n: 'languageChanged loaded added',
      useSuspense: false,
    },
  });

applyDirection(i18n.language);

i18n.on('languageChanged', (lang) => {
  localStorage.setItem('staynivo-lang', lang);
  applyDirection(lang);
  // Load dynamic translations when switching language
  if (lang !== 'en') loadDynamicTranslations(lang);
});

// Guard prevents the languageChanged listener from triggering a second fetch
let _dynamicLoadInProgress = false;

// Load AI/admin-managed translations from DB and merge over bundled defaults
export async function loadDynamicTranslations(lang) {
  if (lang === 'en' || _dynamicLoadInProgress) return;
  _dynamicLoadInProgress = true;
  try {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    const res = await fetch(`${apiBase}/i18n/${lang}`);
    if (!res.ok) return;
    const { data } = await res.json();
    if (!data || !Object.keys(data).length) return;
    const nested = unflatten(data);
    const merged = deepMerge(BASE_RESOURCES[lang] || {}, nested);
    i18n.addResourceBundle(lang, 'translation', merged, true, true);
    // react-i18next v14 only re-renders on 'languageChanged', not 'added'.
    // Emit it so mounted useTranslation components pick up DB overrides.
    if (i18n.language === lang) i18n.emit('languageChanged', lang);
  } catch {
    // Network error or no translations yet — silently fall back to bundled
  } finally {
    _dynamicLoadInProgress = false;
  }
}

// Load dynamic translations for the current language on startup
const currentLang = localStorage.getItem('staynivo-lang') || 'en';
if (currentLang !== 'en') loadDynamicTranslations(currentLang);

export default i18n;
